import type { DetailedHTMLProps, ImgHTMLAttributes } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import FetchUtils from '../../Utils/FetchUtils';
import { omit } from 'lodash-es';
import { useCacheService } from '../../Services/CacheService';

export type CachedImageProps = DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>;

export default function CachedImage(props: CachedImageProps) {
	const { src, alt } = props;

	const [imageUrl, setImageUrl] = useState('');

	const cacheService = useCacheService('images');

	const cacheFirstImageAccessor = useCallback(async () => {
		if (!src) {
			return;
		}

		// React 19's experimental DOM types widen an <img>'s `src` to `string | Blob`
		// (the `srcObject` feature). CachedImage only caches string URLs; hand a Blob
		// straight to an object URL and skip the cache path.
		if (typeof src !== 'string') {
			setImageUrl(URL.createObjectURL(src));
			return;
		}

		if (src.startsWith('data:') || src.startsWith('blob:')) {
			setImageUrl(src);
			return;
		}

		let response = await cacheService.match(src);

		if (!response || !response.ok) {
			const resp = await FetchUtils.get(src, {
				redirect: 'follow',
			}).response;

			if (resp.ok) {
				await cacheService.put(src, resp);
				// Re-read from the cache. We can't re-use imageResponse now, since its body will already be read
				response = await cacheService.match(src);
			}
		}

		if (response && response.ok) {
			const blob = await response.blob();
			const url = URL.createObjectURL(blob);
			setImageUrl(url);
		}
	}, [cacheService, src]);

	useEffect(() => {
		cacheFirstImageAccessor();
	}, [cacheFirstImageAccessor]);

	return <img {...omit(props, 'diContainer')} alt={alt} src={imageUrl} />;
}
